import Page from "@/manager/components/page/page";
import Tooltip from "@/shared/components/tooltip/tooltip";
import { useContext, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { CiSettings } from "react-icons/ci";
import { FiDownload, FiSearch, FiX } from "react-icons/fi";
import AuthContext from "@/shared/context/auth-context";
import { roleAdmin } from "@/shared/constants/user-roles";
import { downloadFilePost, getAllData, getData, postSingle, warningToast } from "@/shared/utils/api";
import useHttp from "@/shared/hooks/use-http";
import { validateLoadingStatusNew } from "@/shared/utils/utils";
import Spinner from "@/shared/components/spinner/spinner";
import Collapsible from "@/manager/components/collapsible/collapsible";
import SimpleModal from "@/shared/components/simple-modal/simple-modal";
import ControlledTable, { IColumn, ITableRowData } from "@/manager/components/controlled-table/controlled-table";
import { noop } from "@/manager/utils";
import { useNavigate } from "react-router";
import AdvancedSearchConfig, { ISelectedConfig } from "./advanced-search-config";
import { useHttpGet, useHttpNonTable } from "@/shared/hooks/use-httpNonTable";
import "./advanced-search.scss";
import { ISortedBy } from "@/manager/components/data-table/data-table";

interface ISelectedSearchField {
  id: string;
  model_type: string;
  model_id?: string;
  field_name?: string;
  field_id?: string;
  field_type?: string;
}

interface ISearchFormValues {
  [key: string]: string | number | boolean | undefined;
}

interface IMatchDetails {
  type: string;
  context: string;
  matched_on: string;
  title: string;
  created_at: string;
}

interface ISearchResultRow extends ITableRowData {
  id: string;
  sygno_alertcase_alertcase_number?: string;
  sygno_alertcase_title?: string;
  sygno_alertcase_domain_type_name?: string;
  sygno_alertcase_process_type_name?: string;
  sygno_alertcase_current_stage_name?: string;
  sygno_alertcase_creation_date?: string;
  tooltips?: IMatchDetails[];
  [key: string]: unknown;
}

interface ISearchResponse {
  rowData: ISearchResultRow[];
  total?: number;
}

interface ISearchPayload {
  rowData: Array<{
    id: string;
    model_type: string;
    model_id?: string;
    field_name?: string;
    field_id?: string;
    field_type?: string;
    search_term?: string;
    search_term_to?: string;
  }>;
}

interface IGroupedFields {
  alertcase: ISelectedSearchField[];
  notes: ISelectedSearchField[];
  documents: ISelectedSearchField[];
  entity: ISelectedSearchField[];
  event: ISelectedSearchField[];
}

const matchDetailsColumns: IColumn[] = [
  {
    key: "type",
    displayName: "Type",
    dataType: {
      type: "text",
    },
  },
  {
    key: "matched_on",
    displayName: "Matched on",
    dataType: {
      type: "text",
    },
  },
  {
    key: "title",
    displayName: "Title",
    dataType: {
      type: "text",
    },
  },
  {
    key: "created_at",
    displayName: "Created at",
    dataType: {
      type: "text",
    },
  },
  {
    key: "context",
    displayName: "Context",
    dataType: {
      type: "text",
    },
  },
];

const alertCaseColumns: IColumn[] = [
  {
    key: "sygno_alertcase_alertcase_number",
    displayName: "AlertCase number",
    dataType: {
      type: "text",
    },
  },
  {
    key: "sygno_alertcase_title",
    displayName: "Title",
    dataType: {
      type: "text",
    },
  },
  {
    key: "sygno_alertcase_domain_type_name",
    displayName: "Domain type",
    dataType: {
      type: "text",
    },
  },
  {
    key: "sygno_alertcase_process_type_name",
    displayName: "Process type",
    dataType: {
      type: "text",
    },
  },
  {
    key: "sygno_alertcase_current_stage_name",
    displayName: "Current stage",
    dataType: {
      type: "text",
    },
  },
  {
    key: "sygno_alertcase_creation_date",
    displayName: "Created at",
    dataType: {
      type: "dateTime",
    },
  },
  {
    key: "sygno_alertcase_consolidation_entity_name",
    displayName: "Consolidation",
    dataType: {
      type: "text",
    },
  },
  {
    key: "sygno_alertcase_assigned_to",
    displayName: "Assigned user",
    dataType: {
      type: "text",
    },
  },
  {
    key: "sygno_alertcase_last_changed_date",
    displayName: "Last changed date",
    dataType: {
      type: "dateTime",
    },
  },
  {
    key: "sygno_alertcase_last_changed_by_id",
    displayName: "Last changed by",
    dataType: {
      type: "text",
    },
  },
  {
    key: "match_details",
    displayName: "Match details",
    dataType: {
      type: "action",
      buttonText: "View",
      shouldShow: (row: ITableRowData) => {
        const searchRow = row as ISearchResultRow;
        return !!(searchRow.tooltips && searchRow.tooltips.length > 0);
      },
      buttonStyle: "none",
      tooltipText: "Click to see match details",
    },
  },
];

const AdvancedSearch = () => {
  const authCtx = useContext(AuthContext);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [selectedConfig, setSelectedConfig] = useState<ISelectedConfig | undefined>(undefined);
  const rolesArray = authCtx.selectedTenant.roles;
  const hasTenantAdminRole = rolesArray.indexOf(roleAdmin) !== -1;
  const [searchFields, setSearchFields] = useState<ISelectedSearchField[]>([]);
  const [formValues, setFormValues] = useState<ISearchFormValues>({});
  const [newFieldsAdded, setNewFieldsAdded] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ISearchResponse | undefined>(undefined);
  const [allResults, setAllResults] = useState<ISearchResultRow[]>([]);
  const [selectedRowTooltips, setSelectedRowTooltips] = useState<IMatchDetails[] | undefined>(undefined);
  const [showMatchDetails, setShowMatchDetails] = useState<boolean>(false);
  const [entityNames, setEntityNames] = useState<{ [key: string]: string }>({});
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({ column: "sygno_alertcase_creation_date", direction: "desc" });
  const [offset, setOffset] = useState<number>(0);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentSearchPayload, setCurrentSearchPayload] = useState<ISearchPayload | undefined>(undefined);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [forceCloseCollapsible, setForceCloseCollapsible] = useState<boolean>(false);
  const navigate = useNavigate();

  const { sendRequest: getAdvancedSearchFields, data: getAdvancedSearchFieldsData, status: getAdvancedSearchFieldsStatus, error: getAdvancedSearchFieldsError } = useHttp(getAllData, false, authCtx.token, "advancedSearchConfig", "manager");
  const { sendRequest: exportSearch, status: exportSearchStatus, data: exportSearchData, error: exportSearchError } = useHttpNonTable(downloadFilePost, false, authCtx.token, "default", "manager");
  const { sendRequest: postAdvancedSearchFields, status: postAdvancedSearchFieldsStatus, data: postAdvancedSearchFieldsData, error: postAdvancedSearchFieldsError } = useHttp(postSingle, true, authCtx.token, "advancedSearch", "manager");
  const { sendRequest: getEntity, data: getEntityData, status: getEntityStatus, error: getEntityError } = useHttp(getAllData, false, authCtx.token, "entity");
  const { sendRequest: getAlertCase, status: getAlertCaseStatus, data: getAlertCaseData, error: getAlertCaseError } = useHttpGet(getData, false, authCtx.token, "alertcaseRecord", "manager");

  useEffect(() => {
    getAdvancedSearchFields("custom_search_fields");
  }, [showSettings]);

  useEffect(() => {
    getAdvancedSearchFields("custom_search_fields");
    setNewFieldsAdded(false);
  }, [newFieldsAdded]);

  useEffect(() => {
    if (getAdvancedSearchFieldsStatus === "completed" && getAdvancedSearchFieldsData && !getAdvancedSearchFieldsError) {
      const fields = getAdvancedSearchFieldsData.rowData || [];
      setSearchFields(fields);

      const entityFields = fields.filter((f: ISelectedSearchField) => f.model_type === "entity" && f.model_id);
      const entityIds: string[] = [];
      entityFields.forEach((f: ISelectedSearchField) => {
        if (f.model_id && !entityIds.includes(f.model_id)) {
          entityIds.push(f.model_id);
        }
      });

      if (entityIds.length > 0) {
        entityIds.forEach((entityId) => {
          getEntity(`datamodel/entity/${entityId}`);
        });
      }
    }
  }, [getAdvancedSearchFieldsStatus, getAdvancedSearchFieldsData, getAdvancedSearchFieldsError]);

  useEffect(() => {
    if (getEntityStatus === "completed" && getEntityData && !getEntityError) {
      const entityId = getEntityData.id;
      const entityName = getEntityData.name;
      setEntityNames((prev) => ({
        ...prev,
        [entityId]: entityName,
      }));
    }
  }, [getEntityStatus, getEntityData, getEntityError]);

  useEffect(() => {
    if (postAdvancedSearchFieldsStatus === "completed" && postAdvancedSearchFieldsData && !postAdvancedSearchFieldsError) {
      const responseData = postAdvancedSearchFieldsData as ISearchResponse;
      setSearchResults(responseData);

      if (offset === 0) {
        setAllResults(responseData.rowData || []);
        const searchResultsElement = document.getElementById("searchResults");
    if (searchResultsElement) {
      searchResultsElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
      } else {
        setAllResults((prev) => [...prev, ...(responseData.rowData || [])]);
        setIsLoadingMore(false);
      }

      const loadedCount = responseData.rowData?.length || 0;
      if (loadedCount < 50) {
        setHasMore(false);
      }
      
      setIsSearching(false);
    }
  }, [postAdvancedSearchFieldsData, postAdvancedSearchFieldsStatus, postAdvancedSearchFieldsError, offset]);

  useEffect(() => {
    if (getAlertCaseStatus === "completed" && getAlertCaseData && !getAlertCaseError) {
      navigate(`/manager/alertcases/${getAlertCaseData.id}`);
    }
  }, [getAlertCaseStatus, getAlertCaseData, getAlertCaseError]);

  useEffect(() => {
    if (currentSearchPayload && sortedByCol.column !== "default") {
      setOffset(0);
      setAllResults([]);
      setHasMore(true);
      performSearch(currentSearchPayload, 0, sortedByCol);
    }
  }, [sortedByCol]);

  const handleSaveConfig = (config: ISelectedConfig) => {
    getAdvancedSearchFields("custom_search_fields");
    setSelectedConfig(config);
    setShowSettings(false);
  };

  const handleInputChange = (fieldKey: string, value: string | number | boolean) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleClearField = (fieldKey: string) => {
    setFormValues((prev) => {
      const newValues = { ...prev };
      delete newValues[fieldKey];
      delete newValues[`${fieldKey}_from`];
      delete newValues[`${fieldKey}_to`];
      return newValues;
    });
  };

  const handleClearAll = () => {
    setFormValues({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setForceCloseCollapsible(true);
    const searchResultsElement = document.getElementById("searchResults");
    if (searchResultsElement) {
      searchResultsElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    handleSearch();
  };

  const performSearch = (payload: ISearchPayload, currentOffset: number, sorting: ISortedBy) => {
    const orderByParam = `&order_by=${sorting.column}%20${sorting.direction}`;
    setIsSearching(true);
    postAdvancedSearchFields(`advanced_search?limit=50&offset=${currentOffset}${orderByParam}`, payload, "", "none");
  };

  const handleSearch = () => {
    const advanced_search_fields = searchFields.map((field) => {
      const fieldKey = `${field.model_type}_${field.field_id || field.field_name}`;
      const isDatetime = (field.field_type || "").toLowerCase() === "datetime";

      if (isDatetime) {
        const fromVal = formValues[`${fieldKey}_from`];
        const toVal = formValues[`${fieldKey}_to`];
        return {
          id: field.id,
          model_type: field.model_type,
          model_id: field.model_id || undefined,
          field_name: field.field_name || undefined,
          field_id: field.field_id || undefined,
          field_type: field.field_type || undefined,
          search_term: fromVal !== undefined && fromVal !== "" ? String(fromVal) : undefined,
          search_term_to: toVal !== undefined && toVal !== "" ? String(toVal) : undefined,
        };
      }

      const searchTerm = formValues[fieldKey];
      return {
        id: field.id,
        model_type: field.model_type,
        model_id: field.model_id || undefined,
        field_name: field.field_name || undefined,
        field_id: field.field_id || undefined,
        field_type: field.field_type || undefined,
        search_term:
          searchTerm !== undefined && searchTerm !== ""
            ? String(searchTerm)
            : undefined,
      };
    });
    const payload: ISearchPayload = {
      rowData: advanced_search_fields,
    };

    setOffset(0);
    setAllResults([]);
    setHasMore(true);
    setCurrentSearchPayload(payload);

    performSearch(payload, 0, sortedByCol);
  };

  const handleScroll = () => {
    if (!isLoadingMore && hasMore && searchResults && searchResults.rowData && searchResults.rowData.length >= 50) {
      const newOffset = offset + 50;
      setOffset(newOffset);
      setIsLoadingMore(true);
      if (currentSearchPayload) {
        performSearch(currentSearchPayload, newOffset, sortedByCol);
      }
    }
  };

  const handleRowClick = async (rowId: string) => {
    await getAlertCase(`alertcase/${rowId}`);
    return false;
  };

  const handleActionClick = (actionKey: string, row: string | ITableRowData) => {
    if (actionKey === "match_details") {
      const rowId = typeof row === "string" ? row : row.id;
      const fullRow = allResults?.find((r) => r.id === rowId);
      setSelectedRowTooltips(fullRow?.tooltips || []);
      setShowMatchDetails(true);
    }
  };

  const renderMatchDetailsBody = () => {
    if (!selectedRowTooltips) {
      return;
    }
    return (
      <ControlledTable
        columns={matchDetailsColumns}
        data={selectedRowTooltips.map((tooltip, idx) => ({
          id: `${tooltip.matched_on}-${idx}`,
          type: tooltip.type,
          context: tooltip.context,
          matched_on: tooltip.matched_on,
          title: tooltip.title,
          created_at: tooltip.created_at,
        }))}
        selectionMode="off"
        onSorted={noop}
        readOnly={true}
        onScroll={noop}
        onRowClick={noop}
      />
    );
  };

  const renderInput = (field: ISelectedSearchField) => {
    const fieldKey = `${field.model_type}_${field.field_id || field.field_name}`;
    const dataType = field.field_type || "text";
    const value = formValues[fieldKey] || "";

    switch (dataType.toLowerCase()) {
      case "datetime": {
        const fromVal = (formValues[`${fieldKey}_from`] || "") as string;
        const toVal = (formValues[`${fieldKey}_to`] || "") as string;
        const hasAnyDate = fromVal || toVal;
        return (
          <div className="advancedSearch-dateRange">
            <div className="advancedSearch-inputWrapper">
              <Form.Control
                type="date"
                value={fromVal}
                onChange={(e) => handleInputChange(`${fieldKey}_from`, e.target.value)}
              />
            </div>
            <span className="advancedSearch-dateRangeSeparator">—</span>
            <div className="advancedSearch-inputWrapper">
              <Form.Control
                type="date"
                value={toVal}
                onChange={(e) => handleInputChange(`${fieldKey}_to`, e.target.value)}
              />
            </div>
            {hasAnyDate && (
              <button
                type="button"
                className="advancedSearch-clearBtn advancedSearch-clearBtnStatic"
                onClick={() => handleClearField(fieldKey)}
                aria-label="Clear field"
              >
                <FiX />
              </button>
            )}
          </div>
        );
      }

      case "integer":
      case "number":
      case "float":
        return (
          <div className="advancedSearch-inputWrapper">
            <Form.Control
              type="number"
              value={value as string}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              placeholder={`Enter ${field.field_name}`}
            />
            {value && (
              <button
                type="button"
                className="advancedSearch-clearBtn"
                onClick={() => handleClearField(fieldKey)}
                aria-label="Clear field"
              >
                <FiX />
              </button>
            )}
          </div>
        );

      case "boolean":
        return (
          <div className="advancedSearch-checkboxWrapper">
            <Form.Check
              type="checkbox"
              checked={value as boolean}
              onChange={(e) => handleInputChange(fieldKey, e.target.checked)}
              label={field.field_name}
            />
          </div>
        );

      default:
        return (
          <div className="advancedSearch-inputWrapper">
            <Form.Control
              type="text"
              value={value as string}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              placeholder={`Enter ${field.field_name}`}
            />
            {value && (
              <button
                type="button"
                className="advancedSearch-clearBtn"
                onClick={() => handleClearField(fieldKey)}
                aria-label="Clear field"
              >
                <FiX />
              </button>
            )}
          </div>
        );
    }
  };

  useEffect(() => {
    if (exportSearchStatus === "completed" && !exportSearchError && exportSearchData) {
      if (exportSearchData.truncated) {
        warningToast("Export limited to 50,000 rows. Refine your search to get all results.");
      }
      const url = window.URL.createObjectURL(exportSearchData.file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", exportSearchData.filename || "advanced_search_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  }, [exportSearchStatus, exportSearchData, exportSearchError]);

  const handleExportCsv = () => {
    if (!currentSearchPayload) return;
    const exportPayload = {
      advanced_search_fields: currentSearchPayload.rowData.map(field => ({
        id: field.id,
        model_type: field.model_type,
        model_id: field.model_id,
        field_name: field.field_name,
        field_id: field.field_id,
        field_type: field.field_type,
        search_term: field.search_term,
        search_term_to: field.search_term_to,
      }))
    };
    exportSearch("advanced_search/export", exportPayload);
  };

  const groupFieldsByType = (): IGroupedFields => {
    const grouped: IGroupedFields = {
      alertcase: [],
      notes: [],
      documents: [],
      entity: [],
      event: [],
    };

    searchFields.forEach((field) => {
      const type = field.model_type === "manager_alertcase" ? "alertcase" : field.model_type;
      if (type in grouped) {
        grouped[type as keyof IGroupedFields].push(field);
      }
    });

    return grouped;
  };

  const groupEntitiesByModelId = (entityFields: ISelectedSearchField[]): { [modelId: string]: ISelectedSearchField[] } => {
    const grouped: { [modelId: string]: ISelectedSearchField[] } = {};

    entityFields.forEach((field) => {
      const modelId = field.model_id || "unknown";
      if (!grouped[modelId]) {
        grouped[modelId] = [];
      }
      grouped[modelId].push(field);
    });

    return grouped;
  };

  const groupedFields = groupFieldsByType();
  const groupedEntities = groupEntitiesByModelId(groupedFields.entity);
  const hasAnyFields = searchFields.length > 0;

  return (
    <Page title={{ text: "Advanced search" }}>
      <div className="advancedSearch">
        <div className="advancedSearch-header">
          {hasTenantAdminRole && (
            <div className="advancedSearch-settings">
              <Tooltip
                content={"Configure search fields"}
                direction="top"
                delay={350}
              >
                <Button onClick={() => setShowSettings(true)}>
                  <CiSettings />
                </Button>
              </Tooltip>
            </div>
          )}
          {(validateLoadingStatusNew([getAdvancedSearchFieldsStatus, getEntityStatus, getAlertCaseStatus, exportSearchStatus]) || isSearching) && <Spinner />}
          <div>
            {!hasAnyFields ? (
              <div className="advancedSearch-card advancedSearch-emptyState">
                <div className="advancedSearch-cardHeader">
                  No search fields configured
                </div>
                <div className="advancedSearch-cardBody">
                  <FiSearch size={48} className="advancedSearch-emptyIcon" />
                  <h5>No search fields configured</h5>
                  <p className="advancedSearch-emptyText">
                    {hasTenantAdminRole
                      ? "Click the 'Configure Fields' button to set up your search fields."
                      : "Contact your administrator to configure search fields."}
                  </p>
                  {hasTenantAdminRole && (
                    <Button
                      variant="primary"
                      onClick={() => setShowSettings(true)}
                      className="advancedSearch-configureBtn"
                    >
                      <CiSettings className="advancedSearch-configureIcon" />
                      Configure fields
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit} className="advancedSearch-content">
                <h4>Search fields</h4>
                <div className="advancedSearch-collapsibles">
                  {groupedFields.alertcase.length > 0 && (
                    <Collapsible
                      title="AlertCase fields"
                      fieldCount={groupedFields.alertcase.length}
                      defaultOpen={true}
                      forceClose={forceCloseCollapsible}
                    >
                      <div className="advancedSearch-fieldsList">
                        {groupedFields.alertcase.map((field) => (
                          <div key={field.id} className="advancedSearch-fieldRow">
                            <div className="advancedSearch-fieldLabel">
                              {field.field_name}
                            </div>
                            <div className="advancedSearch-fieldInput">
                              {renderInput(field)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Collapsible>
                  )}
                  {groupedFields.notes.length > 0 && (
                    <Collapsible
                      title="Notes"
                      fieldCount={groupedFields.notes.length}
                      defaultOpen={false}
                      forceClose={forceCloseCollapsible}
                    >
                      <div className="advancedSearch-fieldsList">
                        {groupedFields.notes.map((field) => (
                          <div key={field.id} className="advancedSearch-fieldRow">
                            <div className="advancedSearch-fieldLabel">
                              {field.field_name}
                            </div>
                            <div className="advancedSearch-fieldInput">
                              {renderInput(field)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Collapsible>
                  )}
                  {groupedFields.documents.length > 0 && (
                    <Collapsible
                      title="Documents"
                      fieldCount={groupedFields.documents.length}
                      defaultOpen={false}
                      forceClose={forceCloseCollapsible}
                    >
                      <div className="advancedSearch-fieldsList">
                        {groupedFields.documents.map((field) => (
                          <div key={field.id} className="advancedSearch-fieldRow">
                            <div className="advancedSearch-fieldLabel">
                              {field.field_name}
                            </div>
                            <div className="advancedSearch-fieldInput">
                              {renderInput(field)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Collapsible>
                  )}
                  {Object.keys(groupedEntities).length > 0 && (
                    <Collapsible
                      title="Consolidating entities"
                      fieldCount={groupedFields.entity.length}
                      defaultOpen={false}
                      forceClose={forceCloseCollapsible}
                    >
                      {Object.entries(groupedEntities).map(([modelId, fields]) => (
                        <Collapsible
                          key={modelId}
                          title={entityNames[modelId] || `Entity ${modelId}`}
                          fieldCount={fields.length}
                          defaultOpen={false}
                          forceClose={forceCloseCollapsible}
                        >
                          <div className="advancedSearch-fieldsList">
                            {fields.map((field) => (
                              <div key={field.id} className="advancedSearch-fieldRow">
                                <div className="advancedSearch-fieldLabel">
                                  {field.field_name}
                                </div>
                                <div className="advancedSearch-fieldInput">
                                  {renderInput(field)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Collapsible>
                      ))}
                    </Collapsible>
                  )}
                </div>
                <div className="advancedSearch-actions">
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearAll}
                    disabled={Object.keys(formValues).length === 0}
                  >
                    Clear all
                  </Button>
                  <Button variant="primary" type="submit">
                    Search
                  </Button>
                </div>
              </Form>
            )}
          </div>
          <AdvancedSearchConfig
            show={showSettings}
            onPopupClosed={() => { setShowSettings(false); }}
            onSave={handleSaveConfig}
            savedConfig={selectedConfig}
            onPostSave={(success: boolean) => {
              if (success) {
                setNewFieldsAdded(true);
              }
            }}
          />
          <div id="searchResults">
            {searchResults && (
              <div>
                <div className="advancedSearch-resultsHeader">
                  <h4 className="advancedSearch-searchResults">Search results</h4>
                  <Button
                    variant="outline-secondary"
                    onClick={handleExportCsv}
                    disabled={allResults.length === 0}
                  >
                    <FiDownload /> Export CSV
                  </Button>
                </div>
                <ControlledTable
                  columns={alertCaseColumns}
                  data={allResults || []}
                  selectionMode="off"
                  onSorted={setSortedByCol}
                  onRowClick={handleRowClick}
                  onActionClick={handleActionClick}
                  readOnly={false}
                  onScroll={handleScroll}
                />
              </div>
            )}
            </div>
          <SimpleModal
            mode="Information"
            onModalClickHandler={() => setShowMatchDetails(false)}
            modalBody={renderMatchDetailsBody()}
            buttonText={"Close"}
            handleModalClosed={() => setShowMatchDetails(false)}
            handleSubmit={() => setShowMatchDetails(false)}
            show={showMatchDetails}
            showCancelButton={false}
          />
        </div>
      </div>
    </Page>
  );
};

export default AdvancedSearch;